import { stringify } from 'lossless-json';

import { VariableState as State } from '@/constants';
import { JSONSchema } from '@/features/variable/settings/validation';
import { VariableAction } from '@/features/variable/store/actions/variable/action-creators';
import { VariableActionType } from '@/features/variable/store/actions/variable/action-types';
import {
  getSchemaDiff,
  getVariableDiff,
  isVariableModified,
} from '@/features/variable/utils';
import { VariableInfo, VariableMetadata } from '@/lib/PlannerDTOs';
import { deepMerge } from '@/utils/object';

export interface VariableState {
  currentVariable: VariableInfo | null;
  previousVariable: VariableInfo | null;
  variable: VariableInfo | null;
  disabled: boolean;
  variableIsValid: boolean;
  jsonSchemaIsValid: boolean;
  variableDiff?: { [key: string]: any };
  schemaDiff?: { [key: string]: any };
  isModified: boolean;
  isLoading: boolean;
  error: Error | null;
}

export const initialVariableState: VariableState = {
  currentVariable: null,
  previousVariable: null,
  variable: null,
  disabled: false,
  variableIsValid: false,
  jsonSchemaIsValid: false,
  isModified: false,
  isLoading: false,
  error: null,
  variableDiff: null,
};

function variableReducer(
  state: VariableState = initialVariableState,
  action: VariableAction,
): VariableState {
  switch (action.type) {
    case VariableActionType.GET_VARIABLE: {
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    }

    case VariableActionType.SET_VARIABLE: {
      const payload = action.payload as {
        data: any;
        isLoading: boolean;
        error: Error | null;
      };

      const currentVariable = new VariableInfo();
      currentVariable.metadata = new VariableMetadata();
      const variable = new VariableInfo();
      variable.metadata = new VariableMetadata();

      const newCurrentVariable = deepMerge(
        currentVariable,
        payload.data?.data,
      ) as VariableInfo;

      return {
        ...state,
        currentVariable: newCurrentVariable,
        variable: deepMerge(variable, payload.data?.data) as VariableInfo,
        isLoading: payload.isLoading,
        jsonSchemaIsValid: true,
        error: payload.error,
        disabled: newCurrentVariable?.state === State.ARCHIVED,
      };
    }

    case VariableActionType.VARIABLE_DETAIL_UPDATE: {
      const updatePayload = action.payload as {
        variable: VariableInfo;
        variableIsValid: boolean;
      };
      const isModified = isVariableModified(
        state.currentVariable,
        updatePayload.variable,
      );

      return {
        ...state,
        variable: updatePayload.variable,
        variableIsValid: updatePayload.variableIsValid,
        isModified,
      };
    }

    case VariableActionType.VARIABLE_JSON_SCHEMA_UPDATE: {
      const updatePayload = action.payload as {
        jsonSchema: JSONSchema | null;
        jsonSchemaIsValid: boolean;
      };

      const updatedVariable = {
        ...state.variable,
        metadata: {
          ...state.variable.metadata,
          jsonSchema: updatePayload?.jsonSchema
            ? stringify(updatePayload.jsonSchema)
            : null,
          ...(updatePayload?.jsonSchema ? { contextualRules: [] } : {}),
        },
      };

      const isModified = isVariableModified(
        state.currentVariable,
        updatedVariable,
      );

      return {
        ...state,
        variable: updatedVariable,
        jsonSchemaIsValid: updatePayload.jsonSchemaIsValid,
        isModified,
      };
    }

    case VariableActionType.UPDATE_VARIABLE_STATE: {
      const updatePayload = action.payload as {
        state: State;
      };

      return {
        ...state,
        variable: {
          ...state.variable,
          state: updatePayload.state,
        },
      };
    }

    case VariableActionType.UPDATE_VARIABLE_PRODUCT_ENTITY: {
      const updatePayload = action.payload as {
        productEntity: string;
      };

      return {
        ...state,
        variable: {
          ...state.variable,
          productEntity: updatePayload.productEntity,
        },
      };
    }

    case VariableActionType.UPDATE_VARIABLE_IS_VALID: {
      const updatePayload = action.payload as {
        isValid: boolean;
      };

      return {
        ...state,
        variableIsValid: updatePayload.isValid,
      };
    }

    case VariableActionType.SET_VARIABLE_DIFF: {
      const updatePayload = action.payload as {
        previousVariable: VariableInfo;
        currentVariable: VariableInfo;
      };

      return {
        ...state,
        previousVariable: updatePayload.previousVariable,
        currentVariable: updatePayload.currentVariable,
        variable: updatePayload.currentVariable,
        disabled: true,
        variableDiff: getVariableDiff(
          updatePayload.previousVariable,
          updatePayload.currentVariable,
        ),
        schemaDiff: getSchemaDiff(
          updatePayload.previousVariable,
          updatePayload.currentVariable,
        ),
      };
    }

    default:
      return state;
  }
}

export default variableReducer;
